import type { Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  Query key factory（ARCHITECTURE §10.2）。
 *
 *  **`as_of` 在每一個資料 key 裡。** 例外只有 `qk.me()` 與 `qk.portfolioSettings()`
 *  （兩者都不是時序資料，理由寫在各自的註解裡），而 query-keys.test.ts 的豁免清單只准有它們。
 *
 *  這不是風格問題。少一個 key 帶 as_of，那個面板就會在倒帶時沿用「今天」的
 *  快取結果——於是畫面上出現一個看起來正常、實際上來自未來的數字，
 *  而 §10.3 那句「倒回去不變的數字就是洩漏」會指著它說「沒問題」。
 *  **偵測器自己說謊是最糟的失效模式。**
 *
 *  所以這裡只有一個 factory，所有 useQuery 都必須從它拿 key，
 *  而 factory 的每一個函式簽名都把 asOf 放在**必填參數**的位置。
 * ========================================================================== */

/** undefined（沒有倒帶）與 '2026-09-16'（明確倒帶到今天）必須是**不同**的 key。 */
type AsOf = string | undefined;

function asOfSeg(asOf: AsOf): string {
  return asOf ?? 'live';
}

export const qk = {
  all: ['stock'] as const,

  status: (asOf: AsOf) => ['stock', 'status', asOfSeg(asOf)] as const,

  registry: (market: Market, asOf: AsOf) => ['stock', 'registry', market, asOfSeg(asOf)] as const,

  /**
   * `indicators` 與 `overlays` 都進 key 的理由：打開「MA60」或換一個籌碼疊圖之後，
   * payload 會多一條線 / 多一個 pane，但 URL、symbol、範圍、mode、as_of 全都沒變。
   * 少了這一段，使用者按下開關會看到快取直接回舊資料——開關按了沒反應，
   * 而且重整之後又好了（最難回報的那一種）。
   */
  ohlcv: (
    market: Market,
    symbol: string,
    range: { from: string; to: string },
    mode: string,
    asOf: AsOf,
    indicators: readonly string[] = [],
    overlays: readonly string[] = [],
    interval: string = '1d',
  ) =>
    [
      'stock',
      market,
      'ohlcv',
      symbol,
      range.from,
      range.to,
      mode,
      asOfSeg(asOf),
      [...indicators].sort().join('|'),
      [...overlays].sort().join('|'),
      interval,
    ] as const,

  /** §11.2 第一個呼叫：個股表頭。 */
  security: (market: Market, symbol: string, asOf: AsOf) =>
    ['stock', market, 'security', symbol, asOfSeg(asOf)] as const,

  /**
   * §11.2 第二個呼叫：四張 KPI 卡。**一個 key，不是四個。**
   * 四張卡永遠一起渲染，拆成四個 key 就會出現「三張新、一張舊」的畫面，
   * 而使用者無從得知哪一張是舊的。
   */
  kpis: (market: Market, symbol: string, window: string, asOf: AsOf) =>
    ['stock', market, 'kpis', symbol, window, asOfSeg(asOf)] as const,

  /**
   * 指標目錄。它跟 registry 一樣是 app 的 schema 而不是資料，
   * 但仍然帶 as_of —— 目錄本身會隨版本增修，而倒帶到去年的畫面不該長出今年才有的指標。
   */
  indicators: (market: Market, asOf: AsOf) =>
    ['stock', 'indicators', market, asOfSeg(asOf)] as const,

  /**
   * 代號搜尋。帶 as_of 的理由比看起來重要：倒帶到 2008 年時，
   * 2015 年才上市的代號不該出現在搜尋結果裡（ref.security_alias 的時間性別名）。
   */
  symbolSearch: (market: Market, q: string, asOf: AsOf) =>
    ['stock', 'symbol-search', market, q, asOfSeg(asOf)] as const,

  /**
   * Metric Studio 的主序列（`/securities/{symbol}/series/{metric_key}`）。
   *
   * 後面兩段是 P3 加的，理由與 `ohlcv` 當初加 `indicators` / `overlays` 逐字相同：
   * 它們**改變 payload 但不改變 URL 以外的任何東西**，漏掉就會命中錯的快取。
   *
   *  · `periodType` —— `Q`（季）與 `Y`（年）是**同一個 metric、同一段窗口、
   *    完全不同的數字**（`fin.statement_fact` 的同一個 `period_end` 兩列都在）。
   *    少了這一段，使用者按「年度」會拿到快取裡的季度序列：畫面不動、
   *    URL 已經寫著 `period_type=Y`，而那是 ROADMAP §6 驗收 #7 要防的那件事本身。
   *  · `overlay` —— `?with=` 換一條疊圖線同樣只改 payload。
   *
   * 兩者省略時都填 `'default'`（＝「由後端的 registry 決定」），
   * 與明確指定的值分得開：`period_type=Q` 與「不帶」在後端是同一個結果，
   * 但在 URL 上是兩個可分享的狀態，快取不該把它們合併。
   */
  series: (
    market: Market,
    symbol: string,
    metricKey: string,
    range: { from: string; to: string },
    asOf: AsOf,
    periodType: string | undefined = undefined,
    overlay: string | undefined = undefined,
  ) =>
    [
      'stock',
      market,
      'series',
      symbol,
      metricKey,
      range.from,
      range.to,
      asOfSeg(asOf),
      periodType ?? 'default',
      overlay ?? 'default',
    ] as const,

  /**
   * `grain='market'` 的序列（`/{market}/market/series/{metric_key}`）。
   *
   * **刻意不重用 `series` 並塞一個假的 symbol。** 大盤序列沒有個股，
   * 借一個佔位字串（`'*'`、`'__market__'`）當 symbol 會讓「這個 key 是哪一種序列」
   * 變成一個要靠讀值才知道的事；而 `invalidateQueries({ queryKey: ['stock', market, 'series'] })`
   * 那一天會連大盤一起掃掉或一起漏掉，取決於當初挑了哪個佔位字串。
   */
  marketSeries: (
    market: Market,
    metricKey: string,
    range: { from: string; to: string },
    asOf: AsOf,
    periodType: string | undefined = undefined,
    overlay: string | undefined = undefined,
  ) =>
    [
      'stock',
      market,
      'market-series',
      metricKey,
      range.from,
      range.to,
      asOfSeg(asOf),
      periodType ?? 'default',
      overlay ?? 'default',
    ] as const,

  /**
   * 盤前摘要（`/api/v1/me/digest`）。
   *
   * **它為什麼帶 as_of。** 這一頁的內容是前一晚 23:00 就寫死在
   * `app.digest_items` 裡的結論，所以 `as_of` 不會改變後端的計算——但它會改變
   * 這一頁要求的是**哪一天**的那份摘要（`routes/$market/digest.tsx` 把
   * `?date=` 缺席時的預設值取成 `as_of`）。少了這一段，時光機倒帶到 2020-03-15
   * 會命中今天早上那份摘要的快取，而畫面上寫著 2020-03-15 ——
   * §10.3 說的「偵測器自己說謊」的標準形狀。
   *
   * `date` 另外進 key，因為使用者可以在沒有倒帶的情況下翻前一天的摘要。
   */
  digest: (asOf: AsOf, date: string | undefined = undefined) =>
    ['stock', 'digest', asOfSeg(asOf), date ?? 'latest'] as const,
  /**
   * 板塊輪動（`/{market}/market/sector-rotation`）。
   */
  sectorRotation: (market: Market, asOf: AsOf) =>
    ['stock', market, 'sector-rotation', asOfSeg(asOf)] as const,
  /** 板塊輪動展望（`/{market}/market/sector-rotation/outlook`）。 */
  /** 板塊／子產業籌碼資金流向（`/market/sector-flow`）。 */
  sectorFlow: (market: Market, asOf: AsOf, day: string | undefined = undefined) =>
    ['stock', market, 'sector-flow', asOfSeg(asOf), day ?? 'latest'] as const,

  sectorOutlook: (market: Market, asOf: AsOf) =>
    ['stock', market, 'sector-outlook', asOfSeg(asOf)] as const,
  /** 板塊成分股動能（`/{market}/market/sector-rotation/{industry_code}/constituents`）。 */
  sectorConstituents: (market: Market, industryCode: string, asOf: AsOf) =>
    ['stock', market, 'sector-constituents', industryCode, asOfSeg(asOf)] as const,
  /** 盤前摘要（市場層級，`/{market}/market/premarket`）。`date` 另進 key：可翻前一天。 */
  premarket: (market: Market, asOf: AsOf, date: string | undefined = undefined) =>
    ['stock', market, 'premarket', asOfSeg(asOf), date ?? 'latest'] as const,

  /**
   * 券商分點（`/tw/securities/{symbol}/broker-branches`）。端點不收 as_of
   * （見 queries.ts 的 brokerBranchesQuery），但 as_of 仍然進 key：倒帶時不共用今天的快取。
   */
  brokerBranches: (symbol: string, asOf: AsOf, date: string | undefined = undefined) =>
    ['stock', 'tw', 'broker-branches', symbol, date ?? 'latest', asOfSeg(asOf)] as const,

  /**
   * 個股的冠軍模型分數卡（`/{market}/securities/{symbol}/prediction`）。端點吃 `?as_of=`，
   * 取 `as_of_date <= as_of` 的最新一筆。回應依工作階段而異（MODEL_OUTPUT_ACCESS=allowlist），
   * 所以它與 `modelHealth` 都**不進硬碟**（`lib/query-persist.ts`）。
   */
  prediction: (market: Market, symbol: string, asOf: AsOf) =>
    ['stock', market, 'prediction', symbol, asOfSeg(asOf)] as const,

  /**
   * 個股關鍵價位事實統計（`/{market}/securities/{symbol}/key-levels`）。
   */
  keyPriceLevels: (market: Market, symbol: string, asOf: AsOf) =>
    ['stock', market, 'key-levels', symbol, asOfSeg(asOf)] as const,

  /**
   * 模型健檢（`/ai/model-health?market=`）。端點不收 as_of（它講的是「模型現在的健康」），
   * 但 as_of 仍然進 key：倒帶時不與今天共用一份快取，理由與 `brokerBranches` 相同。
   */
  modelHealth: (market: Market, asOf: AsOf) =>
    ['stock', market, 'model-health', asOfSeg(asOf)] as const,
  /**
   * 多因子異動雷達（`/{market}/market/action-radar`）。
   */
  actionRadar: (market: Market, asOf: AsOf, actionType: string | undefined = undefined) =>
    ['stock', market, 'action-radar', asOfSeg(asOf), actionType ?? 'all'] as const,
  /**
   * 三大法人買超排行（`/{market}/market/institutional-rank`）。
   */
  institutionalRank: (
    market: Market,
    asOf: AsOf,
    investor: 'foreign' | 'trust' | 'all' = 'foreign',
    view: 'amount' | 'streak' | 'sum5' = 'amount',
    date: string | undefined = undefined,
  ) =>
    [
      'stock',
      market,
      'institutional-rank',
      asOfSeg(asOf),
      investor,
      view,
      date ?? 'latest',
    ] as const,

  /* ---------------------------------------------------------------------------
   *  綁使用者的 key：第二段一律是 `'user'`。
   *
   *  `lib/query-persist.ts` 看到 `queryKey[1] === 'user'` 就**不寫進 IndexedDB**：
   *  登出、換人之後，上一個人的資料不能還留在這台電腦的硬碟上。
   *  登出時 `clearUserCache` 另外把記憶體裡的快取整個清掉。
   * ------------------------------------------------------------------------ */

  /**
   * 目前登入的使用者（`GET /api/v1/me`）。不帶 as_of 的兩個 key 之一（另一個是
   * `portfolioSettings`），也在 query-keys.test.ts 的豁免清單裡：「我是誰」不是時序資料，
   * 倒帶到 2015 年時登入的仍然是現在這個人。帶上 as_of 反而會讓每一次倒帶都多打一次
   * `/me`，而且全域 401 處理（`lib/query-client.ts`）要寫的是**同一個** key。
   */
  me: () => ['stock', 'user', 'me'] as const,

  /** 我的二步驟驗證狀態（`/auth/totp`）。帳號狀態、不是時序資料，與 `me` 同理沒有 as_of。 */
  totpStatus: () => ['stock', 'user', 'totp-status'] as const,

  /** 自選股看板（`/me/watchlist-board?market&as_of`）：報價隨 as_of 倒帶。 */
  watchlistBoard: (market: Market, asOf: AsOf) =>
    ['stock', 'user', 'watchlist-board', market, asOfSeg(asOf)] as const,

  /** 某一檔在不在預設自選股裡（`/me/watchlist-membership?market&symbol&as_of`）。 */
  watchlistMembership: (market: Market, symbol: string, asOf: AsOf) =>
    ['stock', 'user', 'watchlist-membership', market, symbol, asOfSeg(asOf)] as const,

  /** 個股籌碼分佈（張數，`/securities/{symbol}/holder-distribution`）。 */
  holderDistribution: (market: Market, symbol: string, asOf: AsOf) =>
    ['stock', market, 'holder-distribution', symbol, asOfSeg(asOf)] as const,

  /** 個股技術與籌碼狀態（`/securities/{symbol}/analysis`）。 */
  stockAnalysis: (market: Market, symbol: string, asOf: AsOf) =>
    ['stock', market, 'stock-analysis', symbol, asOfSeg(asOf)] as const,

  /** 內部研究頁（`/{market}/research/watch`，僅白名單）。 */
  researchWatch: (market: Market, asOf: AsOf) =>
    ['stock', market, 'research-watch', asOfSeg(asOf)] as const,

  /** 後台稽核紀錄（`/admin/audit-log`）。 */
  adminAudit: (asOf: AsOf) => ['stock', 'user', 'admin-audit', asOfSeg(asOf)] as const,

  /** 後台會員搜尋（`/admin/users`，僅管理員）。 */
  adminUsers: (asOf: AsOf, q: string) => ['stock', 'user', 'admin-users', asOfSeg(asOf), q] as const,

  /** 個人價位提醒清單（`/me/price-alerts`）。 */
  priceAlerts: (asOf: AsOf, market: string | undefined = undefined) =>
    ['stock', 'user', 'price-alerts', asOfSeg(asOf), market ?? 'all'] as const,

  /** 個人價位提醒觸發事件通知（`/me/price-alert-events`）。 */
  priceAlertEvents: (asOf: AsOf, unreadOnly = false) =>
    ['stock', 'user', 'price-alert-events', asOfSeg(asOf), unreadOnly ? 'unread' : 'all'] as const,

  /* ---------------------------------------------------------------------------
   *  庫存股（`/me/portfolio*`）。交易流水帳是某一個人的資料，所以全部在 `'user'` 命名空間。
   *
   *  寫入（新增／修改／刪除交易、改設定）之後以**前綴** invalidate：
   *  `['stock','user','portfolio']`、`['stock','user','portfolio-history']`、
   *  `['stock','user','portfolio-txns']`（見 features/portfolio/invalidate.ts）。
   *  三個前綴刻意是三個不同的第三段：`'portfolio'` 不是 `'portfolio-history'` 的前綴
   *  （TanStack 比的是陣列元素，不是字串），所以每一種都要各自列出來。
   * ------------------------------------------------------------------------ */

  /** 持股與小計（`/me/portfolio?market&as_of`）：收盤價、公司行動都以 as_of 為準。 */
  portfolio: (market: Market, asOf: AsOf) =>
    ['stock', 'user', 'portfolio', market, asOfSeg(asOf)] as const,

  /** 市值／成本走勢（`/me/portfolio/history?market&range&as_of`）。區間進 key。 */
  portfolioHistory: (market: Market, range: string, asOf: AsOf) =>
    ['stock', 'user', 'portfolio-history', market, range, asOfSeg(asOf)] as const,

  /**
   * 交易明細（`/me/portfolio/transactions?market&symbol&before`）。端點不收 as_of，
   * 呼叫端倒帶時以 `before = as_of 的隔天` 截掉之後的交易；as_of 仍然進 key，
   * 理由與 `brokerBranches` 相同：倒帶時不與今天共用一份快取。
   */
  portfolioTxns: (market: Market, symbol: string | undefined, asOf: AsOf) =>
    ['stock', 'user', 'portfolio-txns', market, symbol ?? 'all', asOfSeg(asOf)] as const,

  /**
   * 手續費／稅額試算（`/me/portfolio/cost-estimate`）。費率取自交易日當天的
   * `ref.cost_at`，所以每一個輸入都進 key；as_of 照規矩進 key（端點不收）。
   */
  portfolioCostEstimate: (
    params: {
      market: Market;
      symbol: string;
      side: string;
      tradeDate: string;
      shares: number;
      price: number;
      isDaytrade: boolean;
    },
    asOf: AsOf,
  ) =>
    [
      'stock',
      'user',
      'portfolio-cost-estimate',
      params.market,
      params.symbol,
      params.side,
      params.tradeDate,
      params.shares,
      params.price,
      params.isDaytrade,
      asOfSeg(asOf),
    ] as const,

  /**
   * 庫存股設定（`GET /me/portfolio/settings`）。**不帶 as_of**，與 `me` 同一個理由：
   * 它是使用者現在的偏好（券商折扣、成本法、LINE 推播），不是時序資料——倒帶到去年
   * 看到的仍然是現在這一份設定，帶上 as_of 只會讓每一次倒帶多打一次、而且寫入後要
   * invalidate 的 key 變成一整排。列在 query-keys.test.ts 的 AS_OF_EXEMPT 與
   * asof-contract.test.ts 的 AS_OF_EXEMPT_FACTORIES。
   */
  portfolioSettings: () => ['stock', 'user', 'portfolio-settings'] as const,
} as const;

/**
 * 稽核用：一個 key 裡有沒有 as_of 區段。
 * query-keys.test.ts 用它對 factory 的**每一個**成員做窮舉斷言——
 * 新增一個 key 卻忘了帶 as_of 時，測試會失敗而不是靜靜地出貨一個洩漏。
 */
export function keyCarriesAsOf(key: readonly unknown[], asOf: AsOf): boolean {
  return key.includes(asOfSeg(asOf));
}
